import { useEffect, useMemo, useState } from "react";
import "./AccountStatements.css";
import {
  Select,
  Input,
  Button,
  Plus,
  Tags,
  NoticeBox,
  Checkbox,
  Upload,
} from "../../../ui";
import AppFileNode from "../../components/AppFileNode/AppFileNode";
import DeleteSelectionToolbar from "../../components/DeleteSelectionToolbar/DeleteSelectionToolbar";
import useSelectionDelete from "../../hooks/useSelectionDelete";
import UploadZone from "../../components/UploadZone/UploadZone";
import { DeleteModal } from "../../../modals/Delete";
import { APPS } from "../../utils/ludDocuments.constants";
import {
  formatDate,
  getLudFileName,
  openLudFile,
} from "../../utils/ludDocuments.helpers";
import {
  createLudStatementAPI,
  deleteLudStatementAPI,
} from "../../../../utils/api/ludDocuments";

export default function AccountStatements({
  restaurant,
  lang,
  token,
  refreshRestaurant,
  canManage,
  onError,
}) {
  const ar = lang === "ar";
  const [app, setApp] = useState(restaurant.enabledApps[0] || "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const deletion = useSelectionDelete({
    deleteOne: (id) => deleteLudStatementAPI(token, restaurant.id, id),
    refresh: refreshRestaurant,
    lang,
  });
  const { cancel: cancelDeletion } = deletion;

  // Reset only when the restaurant or its app list actually changes. Depending
  // on the enabledApps array identity reset the form after every data refresh.
  const enabledAppsKey = restaurant.enabledApps.join("|");
  useEffect(() => {
    setApp(restaurant.enabledApps[0] || "");
    setFrom("");
    setTo("");
    setFile(null);
    cancelDeletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant.id, enabledAppsKey]);

  const allStatements = useMemo(
    () =>
      restaurant.enabledApps.flatMap((key) => restaurant.statements[key] || []),
    [restaurant.enabledApps, restaurant.statements],
  );

  const addStatement = async () => {
    if (!app || !from || !to || !file || saving) return;
    setSaving(true);
    try {
      await createLudStatementAPI(token, restaurant.id, {
        file,
        app,
        from,
        to,
      });
      setFrom("");
      setTo("");
      setFile(null);
      await refreshRestaurant();
    } catch (error) {
      onError?.(
        error,
        ar ? "تعذّر إضافة كشف الحساب" : "Could not add the account statement",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {canManage && allStatements.length > 0 && (
        <DeleteSelectionToolbar selection={deletion} lang={lang} />
      )}

      {restaurant.enabledApps.map((key) => {
        const periods = restaurant.statements[key] || [];
        return (
          <AppFileNode
            key={key}
            title={APPS[key].label[lang]}
            subtitle={
              ar
                ? `${periods.length} فترة مضافة`
                : `${periods.length} periods added`
            }
            status={
              periods.length
                ? ar
                  ? `${periods.length} كشف`
                  : `${periods.length} statements`
                : ar
                  ? "لا يوجد"
                  : "None"
            }
            statusVariant={periods.length ? "success" : "neutral"}
          >
            {periods.length ? (
              periods.map((period) => (
                <div className="lud-statement-row" key={period.id}>
                  {deletion.active && (
                    <Checkbox
                      checked={deletion.isSelected(period.id)}
                      onChange={() => deletion.toggle(period.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  )}
                  <button
                    type="button"
                    className="lud-file-link"
                    onClick={() =>
                      openLudFile(period.file, {
                        token,
                        storeId: restaurant.id,
                        kind: "statement",
                        reference: period.id,
                        onError,
                      })
                    }
                  >
                    <strong>
                      {period.file
                        ? getLudFileName({
                            file: { ...period.file, app: key },
                            kind: "statement",
                            reference: period.id,
                            originalName: period.file.originalName,
                          })
                        : ar
                          ? "كشف حساب"
                          : "Account statement"}
                    </strong>
                    <small>
                      {ar ? "من" : "from"} {formatDate(period.from, lang)}{" "}
                      {ar ? "إلى" : "to"} {formatDate(period.to, lang)} —{" "}
                      {ar ? "أُضيف بتاريخ" : "Added on"}{" "}
                      {formatDate(period.addedDate, lang)}
                    </small>
                  </button>
                  <Tags variant="success">{ar ? "سارٍ" : "Valid"}</Tags>
                </div>
              ))
            ) : (
              <NoticeBox variant="info">
                {ar
                  ? "لا توجد كشوفات مضافة لهذا التطبيق — تُرفع فقط عند طلب العميل."
                  : "No statements have been added for this app; they are uploaded only when requested by the client."}
              </NoticeBox>
            )}
          </AppFileNode>
        );
      })}

      {canManage&&<div className="lud-statement-form">
        <h3>{ar ? "إضافة كشف حساب جديد" : "Add a new account statement"}</h3>
        <div className="lud-statement-form__row">
          <Select
            label={ar ? "التطبيق" : "Application"}
            value={app}
            onChange={(event) => setApp(event.target.value)}
            options={restaurant.enabledApps.map((key) => ({
              value: key,
              label: APPS[key].label[lang],
            }))}
          />
          <Input
            label={ar ? "من تاريخ" : "From"}
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
          <Input
            label={ar ? "إلى تاريخ" : "To"}
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <UploadZone
          lang={lang}
          label={
            file
              ? file.name
              : ar
                ? "اختيار ملف كشف الحساب"
                : "Choose statement file"
          }
          icon={<Upload size={20} />}
          onFiles={(files) => setFile(files?.[0] || null)}
          disabled={!app || !from || !to}
        />
        <Button
          className="lud-form-submit"
          icon={<Plus size={17} />}
          variant="primary"
          appearance="filled"
          onClick={addStatement}
          disabled={!app || !from || !to || !file || saving}
          loading={saving}
        >
          {ar ? "إضافة" : "Add"}
        </Button>
      </div>}

      {deletion.confirmOpen && (
        <DeleteModal
          id={null}
          loading={deletion.deleting}
          error={deletion.error}
          handleDelete={deletion.confirm}
          cancel={deletion.closeConfirm}
        />
      )}
    </>
  );
}
